import type * as THREE from 'three';
import { createCanvas, canvasTexture, hexToRgb, rgbString } from './canvas';

export interface PlanetSurfaceOptions {
  readonly base: number;
  readonly accent: number;
  readonly detail?: number;
  readonly continents?: number;
  readonly speckles?: number;
  readonly size?: number;
  readonly seed?: number;
}

export function createPlanetSurface(
  options: PlanetSurfaceOptions
): THREE.CanvasTexture {
  const size = options.size ?? 1024;
  const height = size / 2;
  const { canvas, ctx } = createCanvas(size, height);

  const baseRgb = hexToRgb(options.base);
  const accentRgb = hexToRgb(options.accent);
  const detailRgb = options.detail !== undefined ? hexToRgb(options.detail) : accentRgb;

  ctx.fillStyle = rgbString(baseRgb, 1);
  ctx.fillRect(0, 0, size, height);

  const continents = options.continents ?? 42;
  for (let i = 0; i < continents; i++) {
    const x = Math.random() * size;
    const y = Math.random() * height;
    const r = 30 + Math.random() * 140;
    const alpha = 0.22 + Math.random() * 0.45;
    const grad = ctx.createRadialGradient(x, y, 0, x, y, r);
    const color = Math.random() < 0.7 ? accentRgb : detailRgb;
    grad.addColorStop(0, rgbString(color, alpha));
    grad.addColorStop(0.7, rgbString(color, alpha * 0.45));
    grad.addColorStop(1, rgbString(color, 0));
    ctx.fillStyle = grad;
    ctx.fillRect(x - r, y - r, r * 2, r * 2);
  }

  const speckles = options.speckles ?? 180;
  for (let i = 0; i < speckles; i++) {
    const x = Math.random() * size;
    const y = Math.random() * height;
    const r = 2 + Math.random() * 10;
    const alpha = 0.08 + Math.random() * 0.22;
    const grad = ctx.createRadialGradient(x, y, 0, x, y, r);
    grad.addColorStop(0, rgbString(detailRgb, alpha));
    grad.addColorStop(1, rgbString(detailRgb, 0));
    ctx.fillStyle = grad;
    ctx.fillRect(x - r, y - r, r * 2, r * 2);
  }

  const poleGradient = ctx.createLinearGradient(0, 0, 0, height);
  poleGradient.addColorStop(0, rgbString([255, 255, 255], 0.18));
  poleGradient.addColorStop(0.12, rgbString([255, 255, 255], 0));
  poleGradient.addColorStop(0.88, rgbString([255, 255, 255], 0));
  poleGradient.addColorStop(1, rgbString([255, 255, 255], 0.18));
  ctx.fillStyle = poleGradient;
  ctx.fillRect(0, 0, size, height);

  return canvasTexture(canvas, { repeat: true });
}

export interface CloudLayerOptions {
  readonly density?: number;
  readonly size?: number;
  readonly tint?: number;
}

export function createCloudLayer(
  options: CloudLayerOptions = {}
): THREE.CanvasTexture {
  const size = options.size ?? 1024;
  const height = size / 2;
  const { canvas, ctx } = createCanvas(size, height);
  const tintRgb: [number, number, number] =
    options.tint !== undefined ? hexToRgb(options.tint) : [255, 255, 255];

  ctx.fillStyle = rgbString([0, 0, 0], 1);
  ctx.fillRect(0, 0, size, height);

  const count = Math.floor((options.density ?? 1) * 110);
  for (let i = 0; i < count; i++) {
    const x = Math.random() * size;
    const y = Math.random() * height;
    const r = 18 + Math.random() * 90;
    const alpha = 0.2 + Math.random() * 0.55;
    const grad = ctx.createRadialGradient(x, y, 0, x, y, r);
    grad.addColorStop(0, rgbString(tintRgb, alpha));
    grad.addColorStop(0.7, rgbString(tintRgb, alpha * 0.3));
    grad.addColorStop(1, rgbString(tintRgb, 0));
    ctx.fillStyle = grad;
    ctx.fillRect(x - r, y - r, r * 2, r * 2);
  }

  return canvasTexture(canvas, { repeat: true });
}
